import { useMemo } from "react";
import type { ReactNode } from "react";
import type { ContactsOfferBlock, ContactsOfferCompany, DownloadOfferBlock, StructuredBlock } from "@containers/Hermes/types";
import useActionPanel from "@containers/Hermes/hooks/useActionPanel";
import useOfferTotal from "@containers/Hermes/hooks/useOfferCount";
import { tableHeaders, tableRowCount, tableTitles } from "@containers/Hermes/utils/tableSegments";
import type { ResultsSource } from "@containers/Hermes/hooks/useActionPanel";
import { overLimitMessage } from "@containers/Hermes/utils/actionLimits";
import LimitTip from "./LimitTip";
import { toBookmarkCompanies } from "./BookmarkCard";

/**
 * The action bars of an answer (Zeplin: Hermes_actionbar) — one per table the
 * answer offers actions for. Normally at the end of the answer, below its
 * follow-up suggestions; when several tables have actions, Message renders
 * each table's bar directly beneath that table instead:
 *
 *   records   ▤ Take actions on the above result | ⤓ Download
 *   companies ▤ Take actions on the above result   ☆ Bookmark | ▦ Add to workspace | ▣ Request contacts | ⤓ Download
 *
 * Built from the message's own offers, so a reloaded conversation keeps them:
 * a table's download offer and its contacts offer (the companies the table
 * names) share a `table_index` and so one bar. A table whose download is a
 * company list, or that has a contacts offer, gets a companies bar; any other
 * download gets a records bar. Offers tied to no table (a model-prepared
 * download, or a card scoped to the companies the user named) get a bar each.
 *
 * Clicking an action opens its card in place of the composer (Add to workspace
 * opens the workspace modal instead); every bar is disabled while one is open.
 */

type Group = {
  key: string;
  tableIndex: number | null;
  download?: DownloadOfferBlock;
  contacts?: ContactsOfferBlock;
};

const compactCount = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });

const GENERIC_LABEL = "Take actions on the above result";

function tableIndexOf(block: { table_index?: number; anchor_table?: number }): number | null {
  const index = block.anchor_table ?? block.table_index;
  return typeof index === "number" && index >= 0 ? index : null;
}

/**
 * Rows the answer's table shows for a bar. A bar tied to no table (a download
 * the model prepared itself, or a single-table answer whose offer carries no
 * table index) belongs to the answer's one table when there is only one.
 */
function previewRows(content: string, tableIndex: number | null, tableCount: number): number {
  if (tableIndex !== null) return tableRowCount(content, tableIndex);
  return tableCount === 1 ? tableRowCount(content, 0) : 0;
}

/** "companies" → "company", "records" → "record". */
function singularNoun(noun: string): string {
  return noun.endsWith("ies") ? `${noun.slice(0, -3)}y` : noun.replace(/s$/, "");
}

function isCompanyDownload(block: DownloadOfferBlock): boolean {
  return (block.unit_noun || "").toLowerCase() === "companies";
}

/**
 * The message's offers, grouped into one bar per table plus one per untied
 * offer. `tableCount` is the number of tables in the answer, when known: with
 * one table or none, a single download and a single company card can only be
 * about the same result, so they share one bar.
 */
export function groupActions(blocks: StructuredBlock[], tableCount?: number): Group[] {
  const byTable = new Map<number, Group>();
  const untied: Group[] = [];

  for (const block of blocks) {
    if (block.type === "download_offer" && block.spec_token && !block.hide_bar) {
      const index = tableIndexOf(block);
      if (index === null) untied.push({ key: `download-${untied.length}`, tableIndex: null, download: block });
      else byTable.set(index, { ...(byTable.get(index) ?? { key: `table-${index}`, tableIndex: index }), download: block });
    } else if (block.type === "contacts_offer" && block.companies?.length) {
      const index = tableIndexOf(block);
      if (index === null) untied.push({ key: `contacts-${untied.length}`, tableIndex: null, contacts: block });
      else byTable.set(index, { ...(byTable.get(index) ?? { key: `table-${index}`, tableIndex: index }), contacts: block });
    }
  }

  // Messages saved before contacts offers carried their table: a single-table
  // answer's one untied company card belongs with that table's company download.
  const untiedContacts = untied.filter((group) => group.contacts);
  const companyTables = [...byTable.values()].filter((group) => group.download && isCompanyDownload(group.download) && !group.contacts);
  if (untiedContacts.length === 1 && byTable.size === 1 && companyTables.length === 1 && !untiedContacts[0].contacts?.scoped) {
    companyTables[0].contacts = untiedContacts[0].contacts;
    untied.splice(untied.indexOf(untiedContacts[0]), 1);
  }

  // A download the model prepared itself and a card scoped to the companies the
  // user named are both untied, so they would otherwise get a bar each — two
  // bars under a single table ("download the shipments of apple inc").
  const all = [...byTable.values(), ...untied];
  const downloads = all.filter((group) => group.download);
  const contacts = all.filter((group) => group.contacts);
  if (tableCount !== undefined && tableCount <= 1 && all.length === 2 && downloads.length === 1 && contacts.length === 1 && downloads[0] !== contacts[0]) {
    const [withDownload, withContacts] = [downloads[0], contacts[0]];
    const tableIndex = withDownload.tableIndex ?? withContacts.tableIndex;
    return [{ key: tableIndex === null ? "combined" : `table-${tableIndex}`, tableIndex: tableIndex ?? (tableCount === 1 ? 0 : null), download: withDownload.download, contacts: withContacts.contacts }];
  }

  const ordered = [...[...byTable.values()].sort((a, b) => (a.tableIndex ?? 0) - (b.tableIndex ?? 0)), ...untied];
  // An answer with one table has only one table an offer can be about, so an
  // offer tied to none still belongs beneath it.
  return tableCount === 1 ? ordered.map((group) => (group.tableIndex === null ? { ...group, tableIndex: 0 } : group)) : ordered;
}

type ActionBarsProps = {
  blocks: StructuredBlock[];
  content: string;
  conversationId: string | null;
  messageId: number;
  // Render only this table's bar, directly beneath the table (Message does
  // this when several tables have actions). It needs no name there, so it
  // keeps the plain "above result" label.
  tableIndex?: number;
  // At the end of the answer: tables whose bar was already rendered beneath them.
  excludeTables?: number[];
  // Companies ticked in each table (by table index).
  selectedCompanies?: Record<number, ContactsOfferCompany[]>;
  // Rows ticked in each table (by table index), for the selection bar.
  selectedRows?: Record<number, Set<number>>;
  // The companies each table shows (by table index): what the bar below it acts on by default.
  shownCompanies?: Record<number, ContactsOfferCompany[]>;
  // The bar above a table that appears once rows are ticked: the company actions
  // on just those rows. Nothing is rendered until a row is ticked. (The bar
  // beneath the table always acts on the rows the answer shows.)
  selectionBar?: boolean;
  // Rendered inside the table's card (the table and its actions are one card), so it brings no panel or margin of its own.
  inCard?: boolean;
};

export default function ActionBars({ blocks, content, conversationId, messageId, tableIndex, excludeTables, selectedCompanies, selectedRows, shownCompanies, selectionBar = false, inCard = false }: ActionBarsProps) {
  const { available, isOpen, open, openResults } = useActionPanel();
  const inline = tableIndex !== undefined;
  const allTitles = useMemo(() => tableTitles(content), [content]);
  const groups = useMemo(() => {
    const all = groupActions(blocks, allTitles.length);
    if (inline) return all.filter((group) => group.tableIndex === tableIndex);
    return excludeTables?.length ? all.filter((group) => group.tableIndex === null || !excludeTables.includes(group.tableIndex)) : all;
  }, [blocks, allTitles.length, inline, tableIndex, excludeTables]);
  const titles = !inline && groups.length > 1 ? allTitles : [];

  if (!available || groups.length === 0) return null;

  const bars = groups
    .map((group) => {
      // With several bars, each is named after its table so the user can tell them apart.
      const label = !inline && groups.length > 1 && group.tableIndex !== null ? `Take actions on: ${titles[group.tableIndex] || `Table ${group.tableIndex + 1}`}` : GENERIC_LABEL;
      const { download } = group;
      // Once rows are ticked the bar above the table is the one to use, so this one steps aside.
      if (!selectionBar && group.tableIndex !== null && selectedRows?.[group.tableIndex]?.size) return null;
      const shown = previewRows(content, group.tableIndex, allTitles.length);
      // The selection bar acts on the ticked rows; the bar beneath the table never
      // does — it acts on the rows the answer shows.
      const rows = selectionBar && group.tableIndex !== null ? selectedRows?.[group.tableIndex] : undefined;
      if (selectionBar && !rows?.size) return null;
      const picked = selectionBar && group.tableIndex !== null ? selectedCompanies?.[group.tableIndex] : undefined;
      const shownList = !selectionBar && group.tableIndex !== null ? shownCompanies?.[group.tableIndex] : undefined;
      const contacts = selectionBar ? (group.contacts && picked?.length ? { ...group.contacts, companies: picked } : undefined) : group.contacts && shownList?.length ? { ...group.contacts, companies: shownList } : group.contacts;
      const shownCount = shownList?.length ? toBookmarkCompanies(shownList).length || new Set(shownList.map((company) => company.company_id || company.name)).size : 0;
      const pickedCount = picked?.length ? toBookmarkCompanies(picked).length || new Set(picked.map((company) => company.company_id || company.name)).size : (rows?.size ?? 0);
      const companies = Boolean(contacts) || (download ? isCompanyDownload(download) : false);
      // The table behind the bar, browsable in full from the results panel. It
      // needs the table's download offer: its spec_token is what the rows are
      // read from. A bar with no download offer opens plain cards, as before.
      const source: ResultsSource | undefined = !selectionBar && download?.spec_token ? { download, contacts, previewCount: shown, messageId, previewHeaders: tableHeaders(content, group.tableIndex ?? 0) } : undefined;

      const actions: { id: string; icon: string; text: string; onClick: () => void; disabled?: boolean; title?: string }[] = [];
      // Each company action has a maximum; past it the action is disabled and says so.
      const actionCount = contacts ? toBookmarkCompanies(contacts.companies).length : 0;
      if (contacts && actionCount > 0) {
        const bookmarkLimit = overLimitMessage("bookmark", actionCount);
        const workspaceLimit = overLimitMessage("workspace", actionCount);
        actions.push({ id: "bookmark", icon: "star", text: "Bookmark", disabled: Boolean(bookmarkLimit), title: bookmarkLimit, onClick: () => open({ kind: "bookmark", block: contacts, source, fromSelection: selectionBar }) });
        actions.push({ id: "workspace", icon: "workspaces", text: "Add to workspace", disabled: Boolean(workspaceLimit), title: workspaceLimit, onClick: () => open({ kind: "workspace", block: contacts, messageId, source, fromSelection: selectionBar }) });
      }
      if (contacts && conversationId && messageId > 0) {
        const contactsLimit = overLimitMessage("contacts", actionCount);
        actions.push({ id: "contacts", icon: "account_box", text: "Request contacts", disabled: Boolean(contactsLimit), title: contactsLimit, onClick: () => open({ kind: "contacts", block: contacts, conversationId, messageId, source, fromSelection: selectionBar }) });
      }
      if (download && !selectionBar) {
        actions.push({ id: "download", icon: "cloud_download", text: "Download", onClick: () => open({ kind: "download", block: download, source }) });
      } else if (download && rows?.size && picked?.some((company) => company.master_id)) {
        // Ticked companies download as exactly those companies, wherever they sit in the table.
        const companyIds = [...new Set(picked.map((company) => company.master_id).filter((id): id is string => Boolean(id)))];
        const downloadLimit = overLimitMessage("download", companyIds.length);
        actions.push({ id: "download", icon: "cloud_download", text: "Download", disabled: Boolean(downloadLimit), title: downloadLimit, onClick: () => open({ kind: "download", block: download, source: { download, contacts: group.contacts, previewCount: shown, messageId }, fromSelection: true, companyIds }) });
      } else if (download && rows?.size && download.row_keys?.some(Boolean)) {
        // Ticked rows of a table that is not about companies download by the bucket behind each row, whichever
        // rows they are.
        const tickedKeys = [...rows].map((index) => download.row_keys?.[index] ?? null);
        const rowKeys = tickedKeys.filter((key): key is string => Boolean(key));
        const unmatched = tickedKeys.length - rowKeys.length;
        const keyLimit = overLimitMessage("download", rowKeys.length);
        actions.push({
          id: "download",
          icon: "cloud_download",
          text: "Download",
          disabled: unmatched > 0 || Boolean(keyLimit),
          title: unmatched > 0 ? "Some ticked rows cannot be downloaded one by one. Untick them to download the rest." : keyLimit,
          onClick: () => open({ kind: "download", block: download, source: { download, contacts: group.contacts, previewCount: shown, messageId }, fromSelection: true, rowKeys }),
        });
      } else if (download && rows?.size) {
        // A download is a run of records, so ticked rows can be downloaded only
        // when they are consecutive (the table's rows are the result's first rows).
        const sorted = [...rows].sort((x, y) => x - y);
        const consecutive = sorted[sorted.length - 1] - sorted[0] + 1 === sorted.length;
        actions.push({
          id: "download",
          icon: "cloud_download",
          text: "Download",
          disabled: !consecutive,
          title: consecutive ? undefined : "Tick a consecutive run of rows to download them",
          onClick: () => open({ kind: "download", block: download, source: { download, contacts: group.contacts, previewCount: shown, messageId }, fromSelection: true, initialRange: { from: sorted[0] + 1, to: sorted[sorted.length - 1] + 1 } }),
        });
      }
      // Beneath the bar, when the table has more records than the answer shows:
      // the offer to choose from all of them.
      const noun = picked?.length ? "companies" : download?.unit_noun || "rows";
      const shownLabel = selectionBar ? `Take action on ${pickedCount.toLocaleString()} selected ${pickedCount === 1 ? singularNoun(noun) : noun}` : source && source.previewCount > 0 ? `Take action on ${(shownCount || source.previewCount).toLocaleString()} ${download?.unit_noun || "records"} shown in above table` : label;
      return actions.length ? { key: group.key, label: shownLabel, companies, actions, source } : null;
    })
    .filter(Boolean);

  if (bars.length === 0) return null;

  return (
    <div role="group" aria-label="Actions on this answer" className={`tw-flex tw-flex-col tw-items-start tw-gap-4 ${selectionBar ? "tw-mt-3 tw-mb-3" : inCard ? "" : inline ? "tw-mt-3 tw-mb-5" : "tw-mt-8"}`}>
      {bars.map((bar) => (
        <BarView key={bar.key} bar={bar} inCard={inCard} isOpen={isOpen} openResults={openResults} />
      ))}
    </div>
  );
}

type Bar = {
  key: string;
  label: string;
  companies: boolean;
  actions: { id: string; icon: string; text: string; onClick: () => void; disabled?: boolean; title?: string }[];
  source?: ResultsSource;
};

/**
 * One bar. When its table is the preview of a bigger result, the offer to choose from all of
 * it sits beneath in the same rounded panel (Zeplin: Hermes_Response_Actions_1), reading the
 * real count ("all 5.4K records"); when the preview already holds everything there is no offer.
 */
function BarView({ bar, inCard, isOpen, openResults }: { bar: Bar; inCard: boolean; isOpen: boolean; openResults: (source: ResultsSource) => void }) {
  const total = useOfferTotal(bar.source?.download);
  const offersAll = Boolean(bar.source && bar.source.previewCount > 0 && (total === null || total > bar.source.previewCount));

  return (
    <div className={offersAll ? (inCard ? "tw-flex tw-flex-col tw-items-stretch tw-gap-3 tw-w-full" : "tw-flex tw-flex-col tw-items-stretch tw-gap-3 tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-gray-200 tw-bg-gray-50 tw-p-2 tw-shadow-[0_4px_6px_-2px_rgba(16,24,40,0.03),0_12px_16px_-4px_rgba(16,24,40,0.08)]") : "tw-contents"}>
      {/* The label and the actions wrap as two whole blocks: on a narrow screen the actions move
          to a second line together, rather than one action (with its divider) dropping down alone. */}
      <div className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-x-6 tw-gap-y-1 tw-max-w-full tw-min-h-9 tw-py-[5px] tw-px-[9px] tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-gray-300 tw-bg-white">
        <span className="tw-flex tw-items-center tw-gap-1 tw-min-w-[140px] tw-max-w-full">
          <span aria-hidden="true" className="tw-shrink-0 tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded tw-bg-gray-100">
            <Icon name="database" className="tw-text-gray-700" />
          </span>
          <span className="tw-truncate tw-text-sm tw-leading-5 tw-text-gray-900" title={bar.label}>
            {bar.label}
          </span>
        </span>

        <span className="tw-flex tw-items-center tw-shrink-0">
          {bar.actions.map((action, index) => (
            <span key={action.id} className="tw-flex tw-items-center">
              {/* Records bar: a divider before its one action. Companies bar: dividers between actions only. */}
              {index > 0 ? <Divider className="tw-mx-3 tw-bg-gray-400" /> : bar.companies ? null : <Divider className="tw-mr-3 tw-bg-gray-200" />}
              <LimitTip tip={action.title}>
                <ActionButton icon={action.icon} disabled={isOpen || Boolean(action.disabled)} onClick={action.onClick}>
                  {action.text}
                </ActionButton>
              </LimitTip>
            </span>
          ))}
        </span>
      </div>

      {offersAll ? (
        <p className="tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-leading-5 tw-text-gray-900">
          <span>
            or would you like to customise selection from all <span className="tw-font-semibold">{total === null ? "" : `${compactCount.format(total)} `}records</span> as found in results?
          </span>
          <button type="button" onClick={() => openResults({ ...(bar.source as ResultsSource), startEmpty: true })} disabled={isOpen} className="tw-h-6 tw-px-2 tw-text-xs tw-font-medium tw-rounded tw-border-none tw-bg-primary-purple-600 tw-text-white hover:tw-bg-primary-purple-500 tw-transition-colors tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
            Yes
          </button>
        </p>
      ) : null}
    </div>
  );
}

function ActionButton({ icon, disabled, onClick, children }: { icon: string; disabled: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className="tw-flex tw-items-center tw-gap-1 tw-h-6 tw-p-0 tw-border-none tw-bg-transparent tw-text-sm tw-leading-5 tw-font-medium tw-text-primary-purple-500 hover:tw-text-primary-purple-600 tw-transition-colors tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:hover:tw-text-primary-purple-500">
      <Icon name={icon} className="tw-font-medium" />
      {children}
    </button>
  );
}

function Divider({ className }: { className: string }) {
  return <span aria-hidden="true" className={`tw-w-px tw-h-6 ${className}`} />;
}

/** A Material Symbols glyph (the font is loaded app-wide in index.html). */
function Icon({ name, className = "" }: { name: string; className?: string }) {
  return (
    <span aria-hidden="true" className={`material-symbols-outlined tw-text-[20px] tw-leading-none tw-select-none ${className}`}>
      {name}
    </span>
  );
}
